app.filter = {
    /**
     * Initialize filtering system with configuration
     * @param {Object} config - Filter configuration
     */
    init: (config) => {
        // Check if required selectors.ITEMS exists
        if (!config.selectors?.ITEMS) {
            app.notifications.show('log', 'app.filter: selectors.ITEMS is required');
            return null;
        }

        // Check if ITEMS actually exist in DOM
        const items = document.querySelectorAll(config.selectors.ITEMS);
        if (!items.length) {
            // app.notifications.show('log', 'app.filter: No items found with selector:', config.selectors.ITEMS);
            return null;
        }

        // Check if filter fields exist in DOM
        if (config.filter?.fields) {
            const missingFields = [];
            
            Object.entries(config.filter.fields).forEach(([fieldName, fieldConfig]) => {
                if (fieldConfig.type === 'checkbox') {
                    // For checkboxes, check if at least one exists
                    const checkboxes = document.querySelectorAll(fieldConfig.selector);
                    if (!checkboxes.length) {
                        missingFields.push(`${fieldName} (${fieldConfig.selector})`);
                    }
                } else {
                    // For dropdown/text, check if element exists
                    const element = document.querySelector(fieldConfig.selector);
                    if (!element) {
                        missingFields.push(`${fieldName} (${fieldConfig.selector})`);
                    }
                }
            });

            if (missingFields.length > 0) {
                app.notifications.show('log', 'app.filter: Missing filter fields:', missingFields);
                // Return null if critical fields are missing, or continue with available fields
                // You can decide the behavior - for now, let's continue with available fields
            }
        }

        const instance = app.filter.createInstance(config);
        instance.init();
        return instance;
    },

    /**
     * Create a filter instance with specific configuration
     * @param {Object} config - Filter configuration
     * @returns {Object} Filter instance
     */
    createInstance: (config) => {
        const instance = {
            config: {
                hash: config.hash || 'filter',
                loadingDelay: config.loadingDelay || 300,
                selectors: config.selectors || {},
                filter: config.filter || {},
                on: config.on || {},
                ...config
            },

            // Cached DOM elements
            _cache: {},

            // Current filter state
            state: {},

            // Loading timeout reference for cancellation
            _loadingTimeout: null,

            /**
             * Initialize the filter instance
             */
            init: () => {
                instance.cacheElements();
                instance.loadStateFromHash();
                instance.initializeFields();
                instance.applyFilters(true); // Skip delay on initial load
            },

            /**
             * Cache frequently used DOM elements
             */
            cacheElements: () => {
                const { selectors } = instance.config;
                
                // Cache main selectors
                Object.entries(selectors).forEach(([key, selector]) => {
                    if (key === 'ITEMS') {
                        instance._cache[key] = document.querySelectorAll(selector);
                    } else {
                        instance._cache[key] = document.querySelector(selector);
                    }
                });

                // Cache field selectors
                if (instance.config.filter?.fields) {
                    Object.entries(instance.config.filter.fields).forEach(([fieldName, fieldConfig]) => {
                        if (fieldConfig.selector) {
                            instance._cache[`FIELD_${fieldName.toUpperCase()}`] = document.querySelector(fieldConfig.selector);
                        }
                    });
                }
            },

            /**
             * Load filter state from URL hash
             */
            loadStateFromHash: () => {
                const hashValue = window.HashManager?.state?.[instance.config.hash];
                if (!hashValue) return;

                instance.state = {};
                const parts = Array.isArray(hashValue) ? hashValue : [hashValue];
                
                parts.forEach(part => {
                    const [key, value] = part.split(':');
                    if (!key || !value) return;
                    
                    const fieldConfig = instance.config.filter?.fields?.[key];
                    if (!fieldConfig) return;
                    
                    // Handle different field types
                    if (fieldConfig.type === 'checkbox') {
                        // Parse semicolon-separated values for checkboxes
                        instance.state[key] = value.split(';');
                    } else if (value !== fieldConfig.emptyValue) {
                        // Single value for dropdown/text
                        instance.state[key] = value;
                    }
                });

                // Apply state to form controls
                instance.applyStateToFields();
            },

            /**
             * Apply current state to form fields
             */
            applyStateToFields: () => {
                if (!instance.config.filter?.fields) return;

                Object.entries(instance.config.filter.fields).forEach(([fieldName, fieldConfig]) => {
                    const element = instance._cache[`FIELD_${fieldName.toUpperCase()}`];
                    if (!element) return;

                    const value = instance.state[fieldName];
                    
                    if (fieldConfig.type === 'select' && element.tagName === 'SELECT' && value) {
                        element.value = value;
                    } else if (fieldConfig.type === 'text' && element.tagName === 'INPUT' && value) {
                        element.value = value;
                    } else if (fieldConfig.type === 'checkbox') {
                        // Handle checkbox groups
                        const checkboxes = document.querySelectorAll(fieldConfig.selector);
                        const activeValues = Array.isArray(value) ? value : (value ? [value] : []);
                        
                        checkboxes.forEach(checkbox => {
                            checkbox.checked = activeValues.includes(checkbox.value);
                        });
                    }
                });
            },

            /**
             * Initialize form fields
             */
            initializeFields: () => {
                if (!instance.config.filter?.fields) return;

                Object.entries(instance.config.filter.fields).forEach(([fieldName, fieldConfig]) => {
                    if (fieldConfig.type === 'select') {
                        instance.initializeDropdown(fieldName, fieldConfig);
                    } else if (fieldConfig.type === 'text') {
                        instance.initializeTextInput(fieldName, fieldConfig);
                    } else if (fieldConfig.type === 'checkbox') {
                        instance.initializeCheckboxGroup(fieldName, fieldConfig);
                    }
                });
            },

            /**
             * Initialize dropdown field
             */
            initializeDropdown: (fieldName, fieldConfig) => {
                const element = instance._cache[`FIELD_${fieldName.toUpperCase()}`];
                if (!element || element.tagName !== 'SELECT') return;

                element.addEventListener('change', () => {
                    instance.updateStateFromFields();
                    instance.applyFilters(); // Use delay for user interactions
                });
            },

            /**
             * Initialize text input field (with debounce)
             */
            initializeTextInput: (fieldName, fieldConfig) => {
                const element = instance._cache[`FIELD_${fieldName.toUpperCase()}`];
                if (!element || element.tagName !== 'INPUT') return;

                const debounceDelay = fieldConfig.debounce || 300;
                let debounceTimer;

                element.addEventListener('input', () => {
                    clearTimeout(debounceTimer);
                    debounceTimer = setTimeout(() => {
                        instance.updateStateFromFields();
                        instance.applyFilters();
                    }, debounceDelay);
                });
            },

            /**
             * Initialize checkbox group
             */
            initializeCheckboxGroup: (fieldName, fieldConfig) => {
                const checkboxes = document.querySelectorAll(fieldConfig.selector);
                
                checkboxes.forEach(checkbox => {
                    if (checkbox.type !== 'checkbox') return;
                    
                    checkbox.addEventListener('change', () => {
                        instance.updateStateFromFields();
                        instance.applyFilters();
                    });
                });
            },

            /**
             * Update internal state from form fields
             */
            updateStateFromFields: () => {
                if (!instance.config.filter?.fields) {
                    instance.state = {};
                    return;
                }

                instance.state = {};
                
                Object.entries(instance.config.filter.fields).forEach(([fieldName, fieldConfig]) => {
                    if (fieldConfig.type === 'select') {
                        instance.updateDropdownState(fieldName, fieldConfig);
                    } else if (fieldConfig.type === 'text') {
                        instance.updateTextState(fieldName, fieldConfig);
                    } else if (fieldConfig.type === 'checkbox') {
                        instance.updateCheckboxState(fieldName, fieldConfig);
                    }
                });
            },

            /**
             * Update state from dropdown field
             */
            updateDropdownState: (fieldName, fieldConfig) => {
                const element = instance._cache[`FIELD_${fieldName.toUpperCase()}`];
                if (!element || element.tagName !== 'SELECT') return;

                const value = element.value;
                if (value && value !== fieldConfig.emptyValue) {
                    instance.state[fieldName] = value;
                }
            },

            /**
             * Update state from text input field
             */
            updateTextState: (fieldName, fieldConfig) => {
                const element = instance._cache[`FIELD_${fieldName.toUpperCase()}`];
                if (!element || element.tagName !== 'INPUT') return;

                const value = element.value.trim();
                if (value && value !== fieldConfig.emptyValue) {
                    instance.state[fieldName] = value;
                }
            },

            /**
             * Update state from checkbox group
             */
            updateCheckboxState: (fieldName, fieldConfig) => {
                const checkboxes = document.querySelectorAll(fieldConfig.selector);
                const checkedValues = [];

                checkboxes.forEach(checkbox => {
                    if (checkbox.type === 'checkbox' && checkbox.checked) {
                        checkedValues.push(checkbox.value);
                    }
                });

                if (checkedValues.length > 0) {
                    instance.state[fieldName] = checkedValues;
                }
            },

            /**
             * Build hash state for URL
             * @returns {Object} Hash state object
             */
            buildHashState: () => {
                const hashParts = [];
                
                Object.entries(instance.state).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        // Handle checkbox arrays: fieldname:value1;value2;value3
                        hashParts.push(`${key}:${value.join(';')}`);
                    } else {
                        // Handle single values: fieldname:value
                        hashParts.push(`${key}:${value}`);
                    }
                });
                
                return { 
                    [instance.config.hash]: hashParts.length > 0 ? hashParts.join(',') : false 
                };
            },

            /**
             * Parse and evaluate filter logic expression
             * @param {HTMLElement} item - Item element to test
             * @returns {boolean} True if item matches the logic expression
             */
            evaluateLogic: (item) => {
                const logic = instance.config.filter?.logic;
                if (!logic) return true;

                // Get item data - support both individual data-* attributes and JSON data-filter
                let itemData = {};
                const dataFilter = item.getAttribute('data-filter');
                
                if (dataFilter) {
                    // Parse JSON data-filter attribute
                    try {
                        itemData = JSON.parse(dataFilter);
                    } catch (e) {
                        app.notifications.show('log', 'app.filter: Invalid data-filter JSON:', item, e);
                        return false;
                    }
                } else {
                    // Fallback to individual data-* attributes
                    Object.keys(instance.config.filter.fields || {}).forEach(fieldName => {
                        const attrValue = item.getAttribute(`data-${fieldName}`);
                        if (attrValue) {
                            try {
                                itemData[fieldName] = JSON.parse(attrValue);
                            } catch (e) {
                                itemData[fieldName] = attrValue;
                            }
                        }
                    });
                }

                // Replace field names with actual values in the expression
                let expression = logic;
                
                Object.entries(instance.config.filter.fields || {}).forEach(([fieldName, fieldConfig]) => {
                    const hasFilter = fieldName in instance.state;
                    let matches = true;
                    
                    if (hasFilter) {
                        const filterValue = instance.state[fieldName];
                        const itemValue = itemData[fieldName];
                        
                        if (fieldConfig.type === 'checkbox' && Array.isArray(filterValue)) {
                            // For checkboxes: item must have ALL selected values
                            if (Array.isArray(itemValue)) {
                                matches = filterValue.every(val => itemValue.includes(val));
                            } else {
                                matches = false;
                            }
                        } else if (fieldConfig.type === 'text') {
                            // For text: check if item contains the search term (name or isin)
                            const searchTerm = filterValue.toLowerCase();
                            let searchMatches = false;
                            
                            if (itemValue && typeof itemValue === 'object') {
                                // Search in name and isin fields
                                const name = (itemValue.name || '').toLowerCase();
                                const isin = (itemValue.isin || '').toLowerCase();
                                searchMatches = name.includes(searchTerm) || isin.includes(searchTerm);
                            } else if (typeof itemValue === 'string') {
                                // Simple string search
                                searchMatches = itemValue.toLowerCase().includes(searchTerm);
                            }
                            
                            matches = searchMatches;
                        } else {
                            // For dropdown: exact match
                            matches = String(itemValue) === String(filterValue);
                        }
                    }
                    
                    // Replace field name with boolean result
                    expression = expression.replace(new RegExp(`\\b${fieldName}\\b`, 'g'), matches);
                });

                // Replace logical operators
                expression = expression
                    .replace(/\bAND\b/g, '&&')
                    .replace(/\bOR\b/g, '||');

                try {
                    // Evaluate the expression safely
                    return new Function('return ' + expression)();
                } catch (e) {
                    app.notifications.show('log', 'app.filter: Invalid logic expression:', logic, e);
                    return true;
                }
            },

            /**
             * Show loading state
             */
            showLoading: () => {
                const loadingElement = instance._cache.LOADING;
                if (loadingElement) {
                    loadingElement.style.display = '';
                }
                
                // Hide all products during loading
                const items = instance._cache.ITEMS;
                if (items) {
                    items.forEach(item => {
                        item.style.display = 'none';
                    });
                }
                
                // Hide no results during loading
                const noResultsElement = instance._cache.NO_RESULTS;
                if (noResultsElement) {
                    noResultsElement.style.display = 'none';
                }
            },

            /**
             * Hide loading state
             */
            hideLoading: () => {
                const loadingElement = instance._cache.LOADING;
                if (loadingElement) {
                    loadingElement.style.display = 'none';
                }
            },

            /**
             * Apply all active filters
             * @param {boolean} skipDelay - Skip loading delay (for initial load)
             */
            applyFilters: (skipDelay = false) => {
                // Cancel any pending filter operation
                if (instance._loadingTimeout) {
                    clearTimeout(instance._loadingTimeout);
                    instance._loadingTimeout = null;
                }

                // Update state from fields
                instance.updateStateFromFields();
                
                // Update URL hash
                if (window.HashManager) {
                    const hashState = instance.buildHashState();
                    window.HashManager.updateState(hashState);
                }

                // Call beforeFilter callback immediately (for UI updates like labels)
                if (instance.config.on?.beforeFilter) {
                    instance.config.on.beforeFilter(instance.state);
                }

                const runFiltering = () => {
                    const items = instance._cache.ITEMS;
                    if (!items) return;

                    let visibleCount = 0;
                    
                    // Apply filters to each item
                    let visibleItems = [];
                    items.forEach(item => {
                        const isVisible = Object.keys(instance.state).length === 0 || instance.evaluateLogic(item);
                        item.style.display = isVisible ? '' : 'none';
                        if (isVisible) {
                            visibleCount++
                            visibleItems.push(item);
                        }
                    });
                    
                    // Handle no results element
                    const noResultsElement = instance._cache.NO_RESULTS;
                    if (noResultsElement) {
                        noResultsElement.style.display = visibleCount === 0 ? 'block' : 'none';
                    }

                    // Hide loading
                    instance.hideLoading();
                    
                    // Clear timeout reference
                    instance._loadingTimeout = null;
                    
                    // Call filter callback after filtering is complete
                    if (instance.config.on?.filter) {
                        instance.config.on.filter(visibleCount, items.length, instance.state, visibleItems);
                    } else {
                        app.notifications?.show('log', `${instance.config.hash} filtered: ${visibleCount}/${items.length} visible`);
                    }
                };

                if (skipDelay) {
                    // Run immediately without loading for initial load
                    runFiltering();
                } else {
                    // Show loading and apply filters after delay
                    instance.showLoading();
                    instance._loadingTimeout = setTimeout(runFiltering, instance.config.loadingDelay);
                }
            }
        };

        return instance;
    }
};